import { useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { loginRequest, protectedResources } from "../authConfig";
import OrderForm from "../components/OrderForm";
import SalesPromotionsTable from "../components/salesPromotionsTable";
import { CircularProgress, Tooltip, Box, Typography, Button, Paper } from "@mui/material";
import OrdersToConfirm from "../components/ordersToConfirm";
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ListIcon from '@mui/icons-material/List';
import HistoryIcon from '@mui/icons-material/History';
import { ArrowRight } from "lucide-react";
import translation from "../translations/salesPromotionsPageTranslation";
import { useUser } from "../contexts/UserContext";
import { isTestEnv } from "../globals";
import { BRAND, BRAND_DARK, BRAND_LIGHT_BG } from "../constants/salesPromotionsTheme";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const SalesPromotionsPage = () => {
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [salesPromotions, setSalesPromotions] = useState();
  const [flag, setFlag] = useState(false);
  const [activeView, setActiveView] = useState(null);
  const [contactCodes, setContactCodes] = useState([]);
  const [loadingStates, setLoadingStates] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [duplicatingOrder, setDuplicatingOrder] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [isLoadingAllOrders, setIsLoadingAllOrders] = useState(false);

  const { orderId } = useParams(); // get orderId from the URL
  const navigate = useNavigate(); // for navigation

  const { user } = useUser();
  const email = user?.email || '';

  const isAdmin = user?.accessRights?.admin || user?.isAdmin;
  const isOrderApprover = user?.roles?.some(r => r.SalesPromotionsPage && r?.properties[0]?.SalesPromotions?.OrderApprover === true);
  const isOrderCreator = user?.roles?.some(r => r.SalesPromotionsPage && r?.properties[0]?.SalesPromotions?.OrderCreator === true);

  const columns = [
    { id: 'selectedOrderingCustomer', label: translation.orderingCustomer, minWidth: 170 },
    { id: 'selectedRecivingCustomer', label: translation.receivingCustomer, minWidth: 170 },
    { id: 'site', label: translation.site, minWidth: 170 },
    {
      id: 'address',
      label: translation.deliveryAddress,
      minWidth: 170,
      renderCell: (params) => params.row.address?.address || '',
    },
    { id: 'deliveryDate', label: translation.deliveryDate, minWidth: 170 },
  ];

  const product_columns = [
    { id: 'PARTDES', label: translation.productDescription, minWidth: 170 },
    { id: 'PARTNAME', label: translation.partName, minWidth: 170 },
    { id: 'quantity', label: translation.quantity, minWidth: 170 },
  ];

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  useEffect(() => {
    const getData = async () => {
      try {
        const roleData = await execute(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getRolesOfUser?email=${email}`
        );
        setRoles(roleData);

        const contactCodes = await execute(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/contactCodes`
        );
        setContactCodes(contactCodes);

        const orders = await execute(
          "POST",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getOrdersOfUser`,
          { user }
        );
        setSalesPromotions([...orders]);
      } catch (error) {
        isTestEnv && console.error('Error fetching orders:', error);
      }
    }
    if (email) getData();
  }, [email, flag]);

  useEffect(() => {
    if (salesPromotions) {
      setLoadingStates(Array(salesPromotions.length).fill(false));
      // Set default view based on permissions and URL params
      if (!activeView) {
        if (orderId) {
          // if orderId is in the URL, always show the order list
          setActiveView('list');
        } else if (isOrderCreator) {
          setActiveView('list');
        } else if (isOrderApprover) {
          setActiveView('approve');
        }
      }
    }
  }, [salesPromotions, orderId]);

  const onApprove = async (id) => {
    setLoadingStates((prev) => ({ ...prev, [id]: true }));
    try {
      const resp = await execute(
        "PATCH",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/approveOrder?orderId=${id}&userId=${user._id}`
      );

      if (resp.status === 201) {
        setSalesPromotions(prev => prev.map(order =>
          order._id === id ? { ...order, status: 'Created' } : order
        ));
      } else if (resp.status === 202) {
        alert(`${translation.orderUpdatedByUser} ${resp.user.Email.split("@")[0]}`);
        setSalesPromotions(prev => prev.map(order =>
          order._id === id ? { ...order, status: '' } : order
        ));
      }
      else if (resp.status >= 400) {
        alert(translation.errorFetchingOrders);
      }
    } catch (err) {
      isTestEnv && console.error(err);
      alert(translation.errorFetchingOrders);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: false }));
    }
  };

  const onReject = async (id) => {
    setLoadingStates((prev) => ({ ...prev, [id]: true }));
    try {
      const resp = await execute(
        "PATCH",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/rejectedOrder?orderId=${id}&userId=${user._id}`
      );

      if (resp.status === 201) {
        setSalesPromotions(prev => prev.map(order =>
          order._id === id ? { ...order, status: 'Rejected' } : order
        ));
      } else if (resp.status === 202) {
        alert(`${translation.orderUpdatedByUser} ${resp.user.Email.split("@")[0]}`);
        setSalesPromotions(prev => prev.map(order =>
          order._id === id ? { ...order, status: '' } : order
        ));
      }
    } catch (err) {
      isTestEnv && console.error(err);
      alert(translation.errorFetchingOrders);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: false }));
    }
  };

  // function to return to the normal view
  const handleBackToNormalView = () => {
    navigate('/SalesPromotions');
  };

  // function to handle editing an order
  const handleEditOrder = (order) => {
    console.log(169, order);

    setEditingOrder(order);
    setOpen(true);
  };

  // function to handle duplicating an order
  const handleDuplicateOrder = (order) => {
    console.log('Duplicating order:', order);
    setDuplicatingOrder(order);
    setOpen(true);
  };

  // function to load all orders (for admin)
  const fetchAllOrders = async () => {
    if (allOrders.length > 0) {
      // already loaded - just switch to the view
      setActiveView('allHistory');
      return;
    }

    setIsLoadingAllOrders(true);
    try {
      const orders = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/salesPromotions/getAllOrders`
      );
      setAllOrders(orders);
      setActiveView('allHistory');
    } catch (error) {
      isTestEnv && console.error('Error fetching all orders:', error);
      alert(translation.errorFetchingOrders);
    } finally {
      setIsLoadingAllOrders(false);
    }
  };

  if (!user || !salesPromotions) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="App">
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        p={2}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            border: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* if orderId is set, show a back button */}
          {orderId && (
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                startIcon={<ArrowRight size={18} />}
                onClick={handleBackToNormalView}
                variant="outlined"
                sx={{
                  direction: 'ltr',
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 600,
                  color: BRAND,
                  borderColor: BRAND,
                  '&:hover': { borderColor: BRAND_DARK, bgcolor: BRAND_LIGHT_BG },
                }}
              >
                חזרה לרשימת ההזמנות
              </Button>
              <Typography variant="h6" sx={{ color: BRAND }}>
                צפייה בהזמנה: {orderId}
              </Typography>
            </Box>
          )}

          {/* Icons Row - hide if orderId is set */}
          {!orderId && (
            <Box
              display="flex"
              flexDirection="row"
              gap={4}
              justifyContent="center"
              alignItems="center"
              borderBottom="1px solid #e0e0e0"
              pb={2}
            >
              {isOrderCreator && (
                <>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <ListIcon
                      sx={{
                        color: activeView === 'list' ? BRAND : BRAND_DARK,
                        fontSize: "3.5rem",
                        cursor: "pointer",
                        outline: 'none',
                        '&:focus': {
                          outline: 'none'
                        }
                      }}
                      onClick={() => setActiveView('list')}
                      role="button"
                      tabIndex={0}
                    />
                    <Typography variant="caption">{translation.orderList}</Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <PlaylistAddIcon
                      sx={{
                        color: open ? BRAND : BRAND_DARK,
                        fontSize: "3.5rem",
                        cursor: "pointer",
                        outline: 'none',
                        '&:focus': {
                          outline: 'none'
                        }
                      }}
                      onClick={() => setOpen(true)}
                      role="button"
                      tabIndex={0}
                    />
                    <Typography variant="caption">{translation.addOrder}</Typography>
                  </Box>
                </>
              )}
              {isOrderApprover && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <PlaylistAddCheckIcon
                    sx={{
                      color: activeView === 'approve' ? BRAND : BRAND_DARK,
                      fontSize: "3.5rem",
                      cursor: "pointer",
                      outline: 'none',
                      '&:focus': {
                        outline: 'none'
                      }
                    }}
                    onClick={() => setActiveView('approve')}
                    role="button"
                    tabIndex={0}
                  />
                  <Typography variant="caption">{translation.unapprovedOrders}</Typography>
                </Box>
              )}
              {isAdmin && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <HistoryIcon
                    sx={{
                      color: activeView === 'allHistory' ? BRAND : BRAND_DARK,
                      fontSize: "3.5rem",
                      cursor: isLoadingAllOrders ? "wait" : "pointer",
                      animation: isLoadingAllOrders ? `${spin} 1s linear infinite` : "none",
                      outline: 'none',
                      '&:focus': {
                        outline: 'none'
                      }
                    }}
                    onClick={fetchAllOrders}
                    role="button"
                    tabIndex={0}
                  />
                  <Typography variant="caption">
                    {isLoadingAllOrders ? translation.loading : translation.allOrdersHistory}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Content Area */}
          <Box>
          {activeView === 'approve' && !orderId && (
            <OrdersToConfirm
              loadingStates={loadingStates}
              salesPromotions={salesPromotions.filter(s =>
                s.approverRole &&
                user.roles &&
                s.approverRole.some(roleId => user.roles.some(userRole => userRole._id === roleId)) &&
                s.status === 'Approval Required'
              )}
              onApprove={onApprove}
              onReject={onReject}
              product_columns={product_columns}
              columns={columns}
            />
          )}

          {(activeView === 'list' || orderId) && salesPromotions.length > 0 && (
            <SalesPromotionsTable
              salesPromotions={salesPromotions.filter(s => {
                const userOrders = s.creater === user._id;
                // if orderId is set, show only that order
                if (orderId) {
                  return userOrders && s.ordName === orderId;
                }
                return userOrders;
              })}
              setSalesPromotions={setSalesPromotions}
              product_columns={product_columns}
              columns={columns}
              highlightOrderId={orderId} // pass orderId to the table
              onEditOrder={handleEditOrder} // pass the edit function
              onDuplicateOrder={handleDuplicateOrder} // pass the duplicate function
            />
          )}

          {activeView === 'allHistory' && isAdmin && !orderId && (
            <SalesPromotionsTable
              salesPromotions={allOrders}
              setSalesPromotions={setAllOrders}
              product_columns={product_columns}
              columns={columns}
              isAdminView={true} // admin view mode - no edit/duplicate option
            />
          )}

          {/* if viewing a specific order and it wasn't found */}
          {orderId && salesPromotions.length > 0 && !salesPromotions.some(s => s.creater === user._id && s.ordName === orderId) && (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              minHeight="200px"
              gap={2}
            >
              <Typography variant="h6" color="text.secondary">
                הזמנה {orderId} לא נמצאה
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ייתכן שההזמנה לא קיימת או שאין לך הרשאה לצפות בה
              </Typography>
              <Button
                variant="contained"
                onClick={handleBackToNormalView}
                sx={{
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: BRAND,
                  '&:hover': { bgcolor: BRAND_DARK },
                }}
              >
                חזרה לרשימת ההזמנות
              </Button>
            </Box>
          )}

          {open && !orderId && (
            <OrderForm
              open={open}
              setOpen={(value) => {
                setOpen(value);
                if (!value) {
                  setEditingOrder(null); // clear edit mode when closing the form
                  setDuplicatingOrder(null); // clear duplicate mode when closing the form
                }
              }}
              roles={roles.filter(r => r?.properties[0]?.SalesPromotions?.OrderCreator)}
              contactCodes={contactCodes}
              setSalesPromotions={setSalesPromotions}
              setFlag={setFlag}
              editingOrder={editingOrder} // pass the order to edit
              duplicatingOrder={duplicatingOrder} // pass the order to duplicate
            />
          )}
        </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default SalesPromotionsPage;